import { motion, useReducedMotion } from 'framer-motion'

type Props = { badge: string; ambient: string; chilled: string; frozen: string }

/**
 * AfscaAmbientGauge — "Spectre de température" (B2, AFSCA scope visual).
 *
 * A wide horizontal temperature gauge spanning the card: three zones left→right
 * (frozen → chilled → ambient). A thermometer cursor sweeps from the cold end and
 * settles in the GREEN "ambient" zone (with a gentle overshoot), turning green on
 * arrival — communicating "ambient temperature only". The colder zones are greyed
 * + struck-through. Bespoke to AFSCA. Honours prefers-reduced-motion (static).
 */

const EASE = [0.22, 1, 0.36, 1] as const

// Thumb horizontal position (% of track), sweeping cold → ambient with a settle.
const X_KEYS = ['7%', '85%', '80%', '80%', '84%', '80%']
const X_TIMES = [0, 0.42, 0.58, 0.78, 0.9, 1]
const COLOR_KEYS = ['#363640', '#363640', '#10b981', '#10b981', '#10b981', '#10b981']

export default function AfscaAmbientGauge({ badge, ambient, chilled, frozen }: Props) {
  const reduce = useReducedMotion()

  const ZONES = [
    { key: 'frozen', text: frozen, temp: '−18 °C', active: false },
    { key: 'chilled', text: chilled, temp: '0–8 °C', active: false },
    { key: 'ambient', text: ambient, temp: '≈ 20 °C', active: true },
  ] as const

  return (
    <div className="relative w-full rounded-2xl sm:rounded-3xl border border-ink-100 bg-gradient-to-br from-white via-accent-50/30 to-white overflow-hidden isolate aspect-[4/3] sm:aspect-[16/10]">
      {/* dot pattern */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.32]"
        style={{
          backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
        }}
      />

      {/* breathing halo over the ambient (right) zone */}
      {reduce ? (
        <div
          aria-hidden
          className="absolute right-[14%] top-1/2 -translate-y-1/2 h-52 w-52 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.18), transparent 70%)' }}
        />
      ) : (
        <motion.div
          aria-hidden
          className="absolute right-[14%] top-1/2 -translate-y-1/2 h-52 w-52 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.20), transparent 70%)' }}
          animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 9, ease: 'easeInOut', repeat: Infinity }}
        />
      )}

      {/* top-left badge pill */}
      <div className="absolute left-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full border border-accent-200 bg-accent-50/90 px-2.5 py-1 text-[10px] sm:text-xs text-accent-800 backdrop-blur-sm">
        <span className="relative flex h-1.5 w-1.5">
          {!reduce && (
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60 animate-ping" />
          )}
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
        </span>
        <span className="font-medium">{badge}</span>
      </div>

      {/* gauge */}
      <div className="absolute inset-0 flex flex-col justify-center gap-7 sm:gap-9 px-7 sm:px-14">
        {/* zone labels (aligned to the 3 segments) */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {ZONES.map((z) => (
            <div key={z.key} className="flex justify-center">
              <motion.div
                initial={reduce ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={reduce ? undefined : { duration: 0.5, ease: EASE, delay: z.active ? 0.9 : 0.2 }}
                className={[
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1',
                  z.active
                    ? 'border-emerald-300 bg-emerald-50/90 shadow-[0_10px_26px_-12px_rgba(16,185,129,0.6)]'
                    : 'border-ink-100 bg-ink-100/60',
                ].join(' ')}
              >
                <span
                  aria-hidden
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: z.active ? '#10b981' : '#c4c4cc' }}
                />
                <span
                  className={`text-[11px] sm:text-sm font-semibold ${
                    z.active ? 'text-emerald-800' : 'text-ink-400 line-through decoration-ink-300'
                  }`}
                >
                  {z.text}
                </span>
                <span className={`hidden sm:inline text-[9px] sm:text-[11px] tabular-nums ${z.active ? 'text-emerald-600' : 'text-ink-400'}`}>
                  {z.temp}
                </span>
                {z.active && (
                  <span className="ml-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-500">
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3.4} aria-hidden>
                      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                )}
              </motion.div>
            </div>
          ))}
        </div>

        {/* track + traveling thermometer cursor */}
        <div className="relative">
          {/* cursor */}
          <motion.div
            className="absolute -top-9 sm:-top-11 z-10 flex flex-col items-center"
            style={{ x: '-50%' }}
            initial={reduce ? false : { left: '7%' }}
            animate={reduce ? { left: '80%' } : { left: X_KEYS }}
            transition={reduce ? undefined : { duration: 6, ease: EASE, times: X_TIMES, repeat: Infinity, repeatDelay: 0.5 }}
          >
            <motion.div
              className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full text-white shadow-[0_12px_28px_-10px_rgba(16,185,129,0.7)]"
              initial={reduce ? false : { backgroundColor: '#363640' }}
              animate={reduce ? { backgroundColor: '#10b981' } : { backgroundColor: COLOR_KEYS }}
              transition={reduce ? undefined : { duration: 6, ease: EASE, times: X_TIMES, repeat: Infinity, repeatDelay: 0.5 }}
            >
              {/* thermometer glyph */}
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M14 14.76V5a2 2 0 0 0-4 0v9.76a4 4 0 1 0 4 0z" />
              </svg>
            </motion.div>
            {/* caret pointing down into the track */}
            <span
              aria-hidden
              className="mt-0.5 h-2.5 w-px bg-ink-300"
            />
          </motion.div>

          {/* 3-segment bar */}
          <div className="flex w-full items-stretch gap-1.5 sm:gap-2">
            {/* frozen */}
            <div className="h-3.5 sm:h-4 flex-1 rounded-full bg-ink-200/70" />
            {/* chilled */}
            <div className="h-3.5 sm:h-4 flex-1 rounded-full bg-ink-200/70" />
            {/* ambient (active) */}
            <div className="relative h-3.5 sm:h-4 flex-1 overflow-hidden rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 shadow-[0_0_0_1px_rgba(16,185,129,0.25)]">
              {!reduce && (
                <motion.span
                  aria-hidden
                  className="absolute inset-y-0 w-1/3 bg-white/35 blur-[2px]"
                  animate={{ x: ['-120%', '360%'] }}
                  transition={{ duration: 2.6, ease: 'easeInOut', repeat: Infinity, repeatDelay: 1.2 }}
                />
              )}
            </div>
          </div>
        </div>

        {/* cold → warm spectrum axis */}
        <div className="flex items-center gap-3">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8c8c97" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0">
            <path d="M12 2v20M4.5 7l15 10M19.5 7l-15 10M12 4l-3 2M12 4l3 2M12 20l-3-2M12 20l3-2M4.9 7.4l.4 3.4M4.9 16.6l.4-3.4M19.1 7.4l-.4 3.4M19.1 16.6l-.4-3.4" />
          </svg>
          <div
            aria-hidden
            className="h-1 flex-1 rounded-full"
            style={{ background: 'linear-gradient(to right, #93c5fd, #a5f3fc 38%, #6ee7b7 68%, #10b981)' }}
          />
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0">
            <circle cx="12" cy="12" r="4.5" />
            <path d="M12 2.5v2.5M12 19v2.5M2.5 12H5M19 12h2.5M5 5l1.8 1.8M17.2 17.2L19 19M19 5l-1.8 1.8M6.8 17.2L5 19" />
          </svg>
        </div>
      </div>
    </div>
  )
}
